import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getCurrencySymbol, formatCurrency, formatCompactCurrency } from '../utils/currency';
import { isIOSNative } from '../utils/nativeBridge';
import { useAuth, API } from '../App';
import { useStepUp } from '../components/auth/StepUpProvider';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Bell,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ScrollText,
  Puzzle,
  User, 
  Building2, 
  Globe, 
  Mail,
  Shield,
  CreditCard,
  LogOut,
  Upload,
  Camera,
  Loader2,
  Check,
  CheckCircle2,
  Plus,
  Trash2,
  Edit,
  Building,
  Wallet,
  AlertCircle,
  Calendar,
  Clock,
  Send,
  FileText,
  Play,
  Pause,
  RefreshCw,
  Link2,
  Unlink,
  Calculator,
  Smartphone,
  Phone,
  X,
  Users,
  Package,
  Lightbulb,
  ShieldAlert,
  ArrowUpRight,
  CalendarDays,
  AlertTriangle,
  ArrowRight,
  Scale,
  Archive
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { useMonoConnect } from '../hooks/useMonoConnect';
import { MonoSyncProgress } from '../components/shared/MonoSyncProgress';
import { BankFeedSyncBanner } from '../components/shared/BankFeedSyncBanner';
import AddBankAccountModal from '../components/AddBankAccountModal';
import { BankIntegrationTooltip, getCountryDisplayName } from '../components/BankIntegrationTooltip';
import { 
  PrivacySettingsTab,
  GeneralSettingsTab,
  SecuritySettingsTab,
  ConnectionsSettingsTab,
  BankingSettingsTab,
  TaxSettingsTab
} from '../components/settings';
import StripeConnectSettings from '../components/settings/StripeConnectSettings';
import StatementVault from '../components/settings/StatementVault';
import ArchivedBankAccountsSection from '../components/settings/ArchivedBankAccountsSection';
import RemovedBankAccountsSection from '../components/settings/RemovedBankAccountsSection';
import RemoveBankAccountDialog from '../components/settings/RemoveBankAccountDialog';
import { useMyAccess } from '../hooks/useMyAccess';
import ConfirmDestructive from '../components/common/ConfirmDestructive';
import { AutomationTipsSection } from '../components/AutomationTips';
import SecurityDashboard from '../components/settings/SecurityDashboard';
import BiometricSecurityCard from '../components/settings/BiometricSecurityCard';
import { useIsStandalone } from '../utils/pwa';
import { ScanFace } from 'lucide-react';
import { NotificationPreferencesTab } from '../components/settings/NotificationPreferencesTab';
import ActivityLogTab from '../components/settings/ActivityLogTab';
import AISettingsTab from '../components/settings/AISettingsTab';
import { ProjectsSettingsTab } from '../components/projects/ProjectsSettingsTab';

// Error Boundary for Settings page
// IMPORTANT: Fallback must NOT use DashboardLayout to avoid cascading crashes
class SettingsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Settings page error:', error, info);
    this.setState({ errorInfo: info });
  }
  render() {
    if (this.state.hasError) {
      const errorMsg = this.state.error?.message || 'Unknown error';
      const errorStack = this.state.error?.stack || '';
      // Extract the component name from the stack for quick debugging
      const componentMatch = this.state.errorInfo?.componentStack?.match(/\n\s+at (\w+)/);
      const failingComponent = componentMatch ? componentMatch[1] : 'Unknown';
      return (
        <div className="min-h-dvh bg-background flex flex-col items-center justify-center gap-4 p-8">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <h2 className="text-xl font-semibold text-foreground">Settings could not load</h2>
          <p className="text-muted-foreground text-sm max-w-md text-center">
            Something went wrong loading this page. Please try refreshing your browser.
          </p>
          <div className="flex gap-3">
            <Button onClick={() => window.location.reload()} variant="outline">
              Refresh Page
            </Button>
            <Button onClick={() => window.location.href = '/dashboard'} variant="default" className="bg-[#108981] hover:bg-[#108981]/90">
              Go to Dashboard
            </Button>
          </div>
          {/* Debug info - helps identify the crash */}
          <details className="mt-4 max-w-lg w-full">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
              Technical details (click to expand)
            </summary>
            <div className="mt-2 p-3 bg-muted rounded-lg text-xs font-mono text-muted-foreground overflow-auto max-h-40">
              <p><strong>Error:</strong> {errorMsg}</p>
              <p><strong>Component:</strong> {failingComponent}</p>
              {errorStack && <pre className="mt-2 whitespace-pre-wrap">{errorStack.split('\n').slice(0, 5).join('\n')}</pre>}
            </div>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}

// Per-tab error boundary - isolates tab crashes so only the failing tab shows an error
class TabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error(`Settings tab "${this.props.tabName}" error:`, error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
            <AlertCircle className="w-8 h-8 text-amber-500" />
            <p className="text-sm font-medium text-foreground">This section couldn't load</p>
            <p className="text-xs text-muted-foreground">{this.state.error?.message}</p>
            <Button 
              onClick={() => this.setState({ hasError: false, error: null })} 
              variant="outline" 
              size="sm"
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}

// iter1078 - Mobile settings picker. Replaces the old dropdown selector
// with a 2-column card grid. Every section is immediately visible so
// users don't miss any settings behind a hidden menu. Once a card is
// tapped, the grid collapses and a "← All settings" back button shows
// above the tab content so users can pop back to the picker at any
// time.
const SETTINGS_MOBILE_SECTIONS = [
  { key: 'general', label: 'General', desc: 'Profile, logo, name', icon: Building2, tint: 'text-indigo-600 bg-indigo-50' },
  { key: 'tax', label: 'Tax', desc: 'VAT, WHT, filings', icon: Calculator, tint: 'text-amber-600 bg-amber-50' },
  { key: 'ai-preferences', label: 'AI Preferences', desc: 'Automation & rules', icon: Sparkles, tint: 'text-fuchsia-600 bg-fuchsia-50' },
  { key: 'security', label: 'Security & Privacy', desc: 'Passwords, sessions, data', icon: Shield, tint: 'text-emerald-600 bg-emerald-50' },
  { key: 'banking', label: 'Banking', desc: 'Bank accounts, feeds', icon: Building, tint: 'text-blue-600 bg-blue-50' },
  { key: 'payments', label: 'Payments & Integrations', desc: 'Stripe, connections', icon: CreditCard, tint: 'text-cyan-600 bg-cyan-50' },
  { key: 'schedules', label: 'Reports & Tips', desc: 'Scheduled emails', icon: Send, tint: 'text-teal-600 bg-teal-50' },
  { key: 'notifications', label: 'Notifications', desc: 'Push, email, in-app', icon: Bell, tint: 'text-orange-600 bg-orange-50' },
  { key: 'projects', label: 'Projects', desc: 'Project tracking prefs', icon: Puzzle, tint: 'text-violet-600 bg-violet-50' },
  { key: 'activity-log', label: 'Activity Log', desc: 'What changed and when', icon: ScrollText, tint: 'text-slate-600 bg-slate-100' },
];

const SettingsMobileNav = ({ activeTab, setActiveTab }) => {
  const active = SETTINGS_MOBILE_SECTIONS.find((s) => s.key === activeTab);
  if (activeTab && active) {
    return (
      <button
        type="button"
        onClick={() => setActiveTab('')}
        className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 py-1"
        data-testid="settings-mobile-back-to-list"
      >
        <ChevronLeft className="w-4 h-4" />
        All settings
        <span className="text-slate-400">·</span>
        <span className="text-slate-900">{active.label}</span>
      </button>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="settings-mobile-grid">
      {SETTINGS_MOBILE_SECTIONS.map((s) => (
        <button
          key={s.key}
          type="button"
          onClick={() => setActiveTab(s.key)}
          className="p-3 rounded-xl border bg-white hover:bg-slate-50 active:scale-[0.98] transition-transform text-left flex flex-col gap-2"
          data-testid={`settings-mobile-card-${s.key}`}
        >
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.tint}`}>
            <s.icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 leading-tight">{s.label}</p>
            <p className="text-[11px] text-slate-500 leading-tight mt-0.5 line-clamp-2">{s.desc}</p>
          </div>
        </button>
      ))}
    </div>
  );
};

const SettingsPage = () => {
  const { user, business, logout, refreshBusiness, refreshUser } = useAuth();
  const { requireStepUp } = useStepUp();
  const navigate = useNavigate();
  const isPwa = useIsStandalone();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Deep-link highlight: /settings?tab=<tab>&highlight=<data-highlight-key>
  const highlightKey = searchParams.get('highlight');
  useEffect(() => {
    if (!highlightKey) return;
    let tries = 0;
    const tick = () => {
      let el = document.querySelector(`[data-highlight-key="${highlightKey}"]`);
      if (!el && tries >= 6) el = document.querySelector(`[data-highlight-fallback="${highlightKey}"]`);
      if (!el) { if (tries++ < 20) setTimeout(tick, 250); return; }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('akf-highlight-pulse');
      setTimeout(() => el.classList.remove('akf-highlight-pulse'), 4000);
    };
    const t = setTimeout(tick, 300);
    return () => clearTimeout(t);
  }, [highlightKey]);
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    // Redirect old tab values to their new merged tabs
    const tabRedirects = {
      'privacy': 'security',
      'connections': 'banking',
      'stripe': 'payments',
      'integrations': 'payments',
      'automation': 'schedules',
    };
    if (tabParam) {
      return tabRedirects[tabParam] || tabParam;
    }
    // iter1078 - On first load with no ?tab= param, DESKTOP lands on
    // "general" (matches historical behaviour) while MOBILE opens the
    // card picker (activeTab='') so users see every section instead of
    // a hidden dropdown. Detect via viewport width - safe on the client
    // (SettingsPage is client-rendered only).
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 1023px)').matches) {
      return '';
    }
    return 'general';
  });
  
  // Two-Factor Authentication state
  const [twoFaStatus, setTwoFaStatus] = useState(null);
  const [showAuthenticatorSetup, setShowAuthenticatorSetup] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showSmsSetup, setShowSmsSetup] = useState(false);
  const [authenticatorSetup, setAuthenticatorSetup] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  
  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [nigerianBanks, setNigerianBanks] = useState([]);
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [savingBank, setSavingBank] = useState(false);
  const [deletingBank, setDeletingBank] = useState(null);
  // Remove & Restore (reversible, password-gated, owner/admin/accountant only)
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removedRefreshKey, setRemovedRefreshKey] = useState(0);
  const { role: myRole } = useMyAccess();
  const canRemoveBank = !myRole || ['owner', 'admin', 'accountant'].includes(myRole);
  const [bankOverageWarning, setBankOverageWarning] = useState(false);
  const [bankOverageInfo, setBankOverageInfo] = useState(null);
  const [checkingBankLimit, setCheckingBankLimit] = useState(false);
  
  // Reply-To Email state
  const [replyToEmail, setReplyToEmail] = useState('');
  const [savingReplyTo, setSavingReplyTo] = useState(false);
  
  // Mono (Open Banking) state
  const [monoConfig, setMonoConfig] = useState({ public_key: '', enabled: false });
  const [monoConnections, setMonoConnections] = useState([]);
  const [loadingMono, setLoadingMono] = useState(true);
  const [syncingMono, setSyncingMono] = useState(null);
  
  // Bank form state
  const [bankForm, setBankForm] = useState({
    account_name: '',
    bank_name: '',
    account_number: '',
    currency: 'NGN',
    country: business?.country || 'NG',
    sort_code: '',
    iban: '',
    bic_swift: '',
    routing_number: '',
    // iter1225 - International wire fields (accordion-collapsed in the modal)
    intermediary_bank_name: '',
    intermediary_swift: '',
    beneficiary_bank_address: '',
    beneficiary_address: '',
    reference_instructions: '',
    account_type: '',
    is_default: false
  });
  // iter1225 - live-search state for the bank-name Combobox on the modal.
  const [bankSearchQuery, setBankSearchQuery] = useState('');
  const [bankSearchResults, setBankSearchResults] = useState([]);
  const [bankSearchOpen, setBankSearchOpen] = useState(false);
  const [wireDetailsOpen, setWireDetailsOpen] = useState(false);

  // Scheduled reports state
  const [scheduledReports, setScheduledReports] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sendingReport, setSendingReport] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    report_type: 'pnl',
    frequency: 'weekly',
    recipients: '',
    day_of_week: 0,
    day_of_month: 1,
    time_of_day: '09:00',
    include_comparison: false,
    is_active: true
  });

  const reportTypes = [
    { value: 'pnl', label: 'Profit & Loss' },
    { value: 'balance_sheet', label: 'Balance Sheet' },
    { value: 'cash_flow', label: 'Cash Flow Statement' },
    { value: 'aged_receivables', label: 'Aged Receivables' },
    { value: 'aged_payables', label: 'Aged Payables' }
  ];

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchBankAccounts();
    fetchNigerianBanks();
    fetchScheduledReports();
    fetchMonoConfig();
    fetchMonoConnections();
    fetch2FAStatus();
    fetchEmailSettings();
  }, []);

  // Reply-To Email Functions
  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/business/settings/email`);
      setReplyToEmail(response.data.reply_to_email || '');
    } catch (error) {
      console.log('Email settings not yet configured');
    }
  };

  const saveReplyToEmail = async () => {
    setSavingReplyTo(true);
    try {
      await axios.patch(`${API}/business/settings/email`, {
        reply_to_email: replyToEmail.trim()
      });
      toast.success('Reply-to email saved! Customer replies will now go to this address.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save reply-to email');
    } finally {
      setSavingReplyTo(false);
    }
  };

  // 2FA Functions
  const fetch2FAStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/2fa/status`);
      setTwoFaStatus(response.data);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    }
  };

  const setup2FA = async (method) => {
    try {
      if (method === 'email') {
        await axios.post(`${API}/auth/2fa/setup/email`);
        setShowEmailVerification(true);
        setVerificationCode('');
        toast.success('Verification code sent to your email');
      } else if (method === 'authenticator') {
        const response = await axios.post(`${API}/auth/2fa/setup/authenticator`);
        setAuthenticatorSetup(response.data);
        setShowAuthenticatorSetup(true);
        setVerificationCode('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    }
  };

  const verify2FA = async (method, code) => {
    try {
      await axios.post(`${API}/auth/2fa/verify`, { method, code });
      toast.success(`${method.charAt(0).toUpperCase() + method.slice(1)} 2FA enabled!`);
      fetch2FAStatus();
      setShowAuthenticatorSetup(false);
      setShowEmailVerification(false);
      setShowSmsSetup(false);
      setVerificationCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    }
  };

  const disable2FA = async (method) => {
    const ok = await requireStepUp({ reason: `Disable ${method.toUpperCase()} 2FA on your account` });
    if (!ok) return;
    try {
      await axios.post(`${API}/auth/2fa/disable/${method}`);
      toast.success(`${method.charAt(0).toUpperCase() + method.slice(1)} 2FA disabled`);
      fetch2FAStatus();
    } catch (error) {
      toast.error('Failed to disable 2FA');
    }
  };

  const sendSmsCode = async () => {
    try {
      await axios.post(`${API}/auth/2fa/setup/sms?phone_number=${encodeURIComponent(phoneNumber)}`);
      setSmsCodeSent(true);
      setVerificationCode('');
      toast.success('Verification code sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    }
  };

  const resend2FACode = async (method) => {
    try {
      await axios.post(`${API}/auth/2fa/resend?method=${method}`);
      toast.success('New code sent');
    } catch (error) {
      toast.error('Failed to resend code');
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(`${API}/bank-accounts`);
      setBankAccounts(response.data);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    } finally {
      setLoadingBanks(false);
    }
  };

  const fetchNigerianBanks = async () => {
    try {
      const response = await axios.get(`${API}/bank-accounts/nigerian-banks`);
      setNigerianBanks(response.data);
    } catch (error) {
      console.error('Error fetching Nigerian banks:', error);
    }
  };

  const fetchMonoConfig = async () => {
    try {
      const response = await axios.get(`${API}/mono/config`);
      setMonoConfig(response.data);
    } catch (error) {
      console.error('Error fetching Mono config:', error);
    }
  };

  const fetchMonoConnections = async () => {
    try {
      const response = await axios.get(`${API}/mono/connections`);
      setMonoConnections(response.data);
    } catch (error) {
      console.error('Error fetching Mono connections:', error);
    } finally {
      setLoadingMono(false);
    }
  };

  const syncMonoConnection = async (connectionId) => {
    setSyncingMono(connectionId);
    try {
      const response = await axios.post(`${API}/mono/connections/${connectionId}/sync`);
      toast.success(`Synced ${response.data.transactions_synced} transactions, ${response.data.transactions_matched} matched`);
      fetchMonoConnections();
    } catch (error) {
      toast.error('Failed to sync bank data');
    } finally {
      setSyncingMono(null);
    }
  };

  // ── Disconnect confirmation state (same warning as My Banks page) ──
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);

  const promptDisconnectMono = (conn) => {
    setDisconnectTarget({
      connectionId: conn.id,
      bankName: conn.institution_name || conn.bank_name || 'this bank'
    });
  };

  const confirmDisconnectMono = async () => {
    if (!disconnectTarget) return;
    setDisconnecting(true);
    try {
      await axios.delete(`${API}/mono/connections/${disconnectTarget.connectionId}`);
      toast.success('Bank disconnected. Your existing transactions and GL accounts are preserved.');
      setDisconnectTarget(null);
      fetchMonoConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect bank');
    } finally {
      setDisconnecting(false);
    }
  };

  // Unified Mono connect hook - one code path for all connect flows
  const {
    connectBank: monoConnectBank,
    confirmBacklog: hookConfirmBacklog,
    dismissBacklog: hookDismissBacklog,
    rollbackConnection: hookRollbackConnection,
    connecting: monoConnecting,
    confirmingBacklog: monoConfirmingBacklog,
    syncStatus: monoSyncStatus,
    syncMessage: monoSyncMessage,
    backlogInfo: monoBacklogInfo,
    resetState: monoResetState,
    preflightData: monoPreflightData,
    preflightLoading: monoPreflightLoading
  } = useMonoConnect({ onSuccess: fetchMonoConnections, user });

  const openMonoWidget = () => {
    monoConnectBank(monoConfig.public_key);
  };

  const fetchScheduledReports = async () => {
    try {
      const response = await axios.get(`${API}/scheduled-reports`);
      setScheduledReports(response.data?.schedules || response.data || []);
    } catch (error) {
      console.error('Error fetching scheduled reports:', error);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const openAddScheduleModal = () => {
    setEditingSchedule(null);
    setScheduleForm({
      report_type: 'pnl',
      frequency: 'weekly',
      recipients: user?.email || '',
      day_of_week: 0,
      day_of_month: 1,
      time_of_day: '09:00',
      include_comparison: false,
      is_active: true
    });
    setScheduleModalOpen(true);
  };

  const openEditScheduleModal = (schedule) => {
    setEditingSchedule(schedule);
    setScheduleForm({
      report_type: schedule.report_type,
      frequency: schedule.frequency,
      recipients: schedule.recipients?.join(', ') || '',
      day_of_week: schedule.day_of_week || 0,
      day_of_month: schedule.day_of_month || 1,
      time_of_day: schedule.time_of_day || '09:00',
      include_comparison: schedule.include_comparison || false,
      is_active: schedule.is_active
    });
    setScheduleModalOpen(true);
  };

  const handleSaveSchedule = async () => {
    const recipients = scheduleForm.recipients.split(',').map(e => e.trim()).filter(e => e);
    if (recipients.length === 0) {
      toast.error('Please add at least one recipient email');
      return;
    }

    setSavingSchedule(true);
    try {
      const payload = {
        ...scheduleForm,
        recipients
      };

      if (editingSchedule) {
        await axios.put(`${API}/scheduled-reports/${editingSchedule.id}`, payload);
        toast.success('Schedule updated!');
      } else {
        await axios.post(`${API}/scheduled-reports`, payload);
        toast.success('Report scheduled!');
      }
      setScheduleModalOpen(false);
      fetchScheduledReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axios.delete(`${API}/scheduled-reports/${scheduleId}`);
      toast.success('Schedule deleted');
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleToggleSchedule = async (scheduleId) => {
    try {
      const response = await axios.post(`${API}/scheduled-reports/${scheduleId}/toggle`);
      toast.success(response.data.message);
      fetchScheduledReports();
    } catch (error) {
      toast.error('Failed to toggle schedule');
    }
  };

  const handleSendNow = async (scheduleId) => {
    setSendingReport(scheduleId);
    try {
      const response = await axios.post(`${API}/scheduled-reports/${scheduleId}/send-now`);
      toast.success(response.data.message);
      fetchScheduledReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send report');
    } finally {
      setSendingReport(null);
    }
  };

  const formatNextRun = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleLogout = () => {
    logout();
  };

  const getCountryName = (code) => {
    const countries = {
      'NG': 'Nigeria',
      'GB': 'United Kingdom',
      'US': 'United States',
      'DE': 'Germany',
      'FR': 'France',
      'GH': 'Ghana',
      'KE': 'Kenya',
      'ZA': 'South Africa'
    };
    return countries[code] || code;
  };

  const getCountryFlag = (code) => {
    const flags = {
      'NG': '🇳🇬',
      'GB': '🇬🇧',
      'US': '🇺🇸',
      'DE': '🇩🇪',
      'FR': '🇫🇷',
      'GH': '🇬🇭',
      'KE': '🇰🇪',
      'ZA': '🇿🇦'
    };
    return flags[code] || '🌍';
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/business/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Logo updated successfully!');
      refreshBusiness();
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const openAddBankModal = async () => {
    setEditingBank(null);
    setBankForm({
      account_name: '',
      bank_name: '',
      account_number: '',
      currency: business?.country === 'NG' ? 'NGN' : business?.country === 'GB' ? 'GBP' : business?.country === 'US' ? 'USD' : 'EUR',
      country: business?.country || 'NG',
      sort_code: '',
      iban: '',
      bic_swift: '',
      routing_number: '',
      account_type: '',
      is_default: bankAccounts.length === 0
    });

    // Check limit before opening
    setCheckingBankLimit(true);
    try {
      const res = await axios.get(`${API}/billing/check-limit/bank_account`);
      // iOS App Store build: never show fee copy - banks keep today's
      // post-paid behaviour, so proceed straight to the connection.
      if (res.data.within_plan || isIOSNative()) {
        setBankModalOpen(true);
      } else {
        setBankOverageInfo(res.data);
        setBankOverageWarning(true);
      }
    } catch {
      setBankModalOpen(true);
    } finally {
      setCheckingBankLimit(false);
    }
  };

  const openEditBankModal = (bank) => {
    setEditingBank(bank);
    setBankForm({
      account_name: bank.account_name || '',
      bank_name: bank.bank_name || '',
      account_number: bank.account_number || '',
      currency: bank.currency || 'NGN',
      country: bank.country || 'NG',
      sort_code: bank.sort_code || '',
      iban: bank.iban || '',
      bic_swift: bank.bic_swift || '',
      routing_number: bank.routing_number || '',
      account_type: bank.account_type || '',
      is_default: bank.is_default || false
    });
    setBankModalOpen(true);
  };

  const handleSaveBank = async () => {
    if (!bankForm.account_name || !bankForm.bank_name || !bankForm.account_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingBank(true);
    try {
      if (editingBank) {
        await axios.put(`${API}/bank-accounts/${editingBank.id}`, bankForm);
        toast.success('Bank account updated!');
      } else {
        await axios.post(`${API}/bank-accounts`, bankForm);
        toast.success('Bank account added!');
      }
      setBankModalOpen(false);
      fetchBankAccounts();
    } catch (error) {
      // iter1126 - surface the strict-edit lockdown clearly. Backend
      // returns 409 `{code: 'bank_account_has_history', locked_fields, message}`
      // when a user tries to mutate currency / opening_balance / etc.
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'bank_account_has_history') {
        toast.error(detail.message || 'This bank account has history and cannot be changed', {
          description: detail.locked_fields?.length
            ? `Locked fields: ${detail.locked_fields.join(', ')}. Archive & recreate to change them.`
            : undefined,
          duration: 8000,
        });
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to save bank account');
        toast.error(msg);
      }
    } finally {
      setSavingBank(false);
    }
  };

  const handleDeleteBank = async (bankId) => {
    setDeletingBank(bankId);
    try {
      await axios.delete(`${API}/bank-accounts/${bankId}`);
      toast.success('Bank account deleted');
      fetchBankAccounts();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.error_code === 'has_journal_history') {
        // iter1113 - can no longer delete accounts with posted history.
        // Guide the user to Archive instead (button lives on the same row).
        toast.error(
          detail.message ||
          'This account has posted journal entries and cannot be deleted. Use Archive instead - the archive icon is on the same row.',
          { duration: 8000 }
        );
      } else {
        toast.error(detail?.message || detail || 'Failed to delete bank account');
      }
    } finally {
      setDeletingBank(null);
    }
  };

  // iter1113 UI polish - Archive is the primary non-destructive action for
  // bank accounts. Restricted to Settings > Banking (this component). Not
  // exposed on /banks. NO typed-name confirmation - a single amber archive
  // icon click + a clear dialog is enough for a reversible action.
  const [pendingArchiveBank, setPendingArchiveBank] = useState(null); // { id, name, isConnected }
  const [archivingBank, setArchivingBank] = useState(null);

  const openArchiveDialog = (bank) => {
    const isConnected = !!(
      bank.plaid_account_id ||
      bank.mono_account_id ||
      bank.stitch_account_id ||
      bank.open_banking_connected ||
      (bank.source && bank.source !== 'manual')
    );
    setPendingArchiveBank({
      id: bank.id,
      name: bank.bank_name || bank.account_name || 'this bank account',
      isConnected,
    });
  };

  const handleArchiveBank = async () => {
    if (!pendingArchiveBank?.id) return;
    setArchivingBank(pendingArchiveBank.id);
    try {
      const res = await axios.post(`${API}/bank-accounts/${pendingArchiveBank.id}/archive`);
      const provider = res?.data?.archived_from_provider;
      toast.success(
        provider
          ? `Archived. ${pendingArchiveBank.name} was disconnected from ${provider} and hidden from future postings.`
          : `Archived. ${pendingArchiveBank.name} is hidden from future postings.`
      );
      setPendingArchiveBank(null);
      fetchBankAccounts();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail?.message ||
        error?.response?.data?.detail ||
        'Failed to archive bank account'
      );
    } finally {
      setArchivingBank(null);
    }
  };

  const getLocalCurrencySymbol = (currency) => {
    return getCurrencySymbol(currency || business?.currency || 'NGN');
  };

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-8 max-w-5xl mx-auto" data-testid="settings-container">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-xl lg:text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-sm lg:text-base text-muted-foreground mt-1">{isIOSNative() ? 'Manage your account, business, and integration settings' : 'Manage your account, business, and payment settings'}</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* iter1078 - Mobile: card grid instead of a dropdown.
              A dropdown was easy to miss (users didn't realise there were
              10 sections behind it). Now every section is visible as a
              tap-target on first load. When a card is picked the grid
              collapses and a "← All settings" back-button appears above
              the content so users can jump back to the picker. */}
          <div className="lg:hidden">
            <SettingsMobileNav activeTab={activeTab} setActiveTab={setActiveTab} />
          </div>
          {/* Desktop: Horizontal tab strip */}
          <TabsList className="hidden lg:inline-flex w-auto overflow-x-auto">
            <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
            <TabsTrigger value="tax" data-testid="tab-tax">Tax</TabsTrigger>
            <TabsTrigger value="ai-preferences" data-testid="tab-ai-preferences">AI Preferences</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">Security & Privacy</TabsTrigger>
            <TabsTrigger value="banking" data-testid="tab-banking">Banking</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments & Integrations</TabsTrigger>
            <TabsTrigger value="schedules" data-testid="tab-schedules">Reports & Tips</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="activity-log" data-testid="tab-activity-log">Activity Log</TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <TabErrorBoundary tabName="general">
              <GeneralSettingsTab 
                user={user} 
                business={business} 
                refreshBusiness={refreshBusiness}
                refreshUser={refreshUser}
                logout={handleLogout}
              />
            </TabErrorBoundary>
          </TabsContent>


          {/* Tax Tab - AI-Powered Tax Management */}
          <TabsContent value="tax" className="space-y-6">
            <TabErrorBoundary tabName="tax">
              <TaxSettingsTab 
                business={business} 
                refreshBusiness={refreshBusiness}
              />
            </TabErrorBoundary>
          </TabsContent>

          {/* AI Preferences Tab - Phase A / #3 */}
          <TabsContent value="ai-preferences" className="space-y-6">
            <TabErrorBoundary tabName="ai-preferences">
              <AISettingsTab />
            </TabErrorBoundary>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <TabErrorBoundary tabName="security">
            {/* Security Monitoring Dashboard */}
            <SecurityDashboard />

            {/* Biometric / device security - PWA-only. On regular web (PC or
                mobile web) Face ID / passkey UX would be surprising and
                many browsers don't reliably re-issue the same credential
                across sessions. Show a hint to install the app instead. */}
            {isPwa ? (
              <BiometricSecurityCard />
            ) : (
              <div
                data-testid="biometric-pwa-only-hint"
                className="p-4 rounded-xl border border-slate-200 bg-slate-50 flex items-start gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-[#108981]/10 flex items-center justify-center shrink-0">
                  <ScanFace className="w-5 h-5 text-[#108981]" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-800 text-sm">
                    Face ID and fingerprint sign-in
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    Unlock with Face ID or fingerprint. Installed app only.
                  </p>
                </div>
              </div>
            )}

            {/* Section Divider: Two-Factor Authentication */}
            <div className="flex items-center gap-2 pt-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Two-Factor Authentication</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <Card data-testid="2fa-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#283F8B]" />
                  Two-Factor Authentication
                </CardTitle>
                <CardDescription>
                  Add an extra layer of security to your account
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Email 2FA */}
                <div className="flex items-center justify-between p-4 border rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">Email Verification</p>
                      <p className="text-sm text-muted-foreground">Receive a code via email when logging in</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {twoFaStatus?.email_enabled ? (
                      <>
                        <Badge className="bg-green-500">Enabled</Badge>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => disable2FA('email')}
                          data-testid="disable-email-2fa"
                        >
                          Disable
                        </Button>
                      </>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setup2FA('email')}
                        data-testid="enable-email-2fa"
                      >
                        Enable
                      </Button>
                    )}
                  </div>
                </div>

                {/* Authenticator 2FA */}
                <div className="flex items-center justify-between p-4 border rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">Authenticator App</p>
                      <p className="text-sm text-muted-foreground">Use Google Authenticator or similar apps</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {twoFaStatus?.authenticator_enabled ? (
                      <>
                        <Badge className="bg-green-500">Enabled</Badge>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => disable2FA('authenticator')}
                          data-testid="disable-auth-2fa"
                        >
                          Disable
                        </Button>
                      </>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setup2FA('authenticator')}
                        data-testid="enable-auth-2fa"
                      >
                        Enable
                      </Button>
                    )}
                  </div>
                </div>

                {/* Security Info */}
                <div className="p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Why use 2FA?</p>
                      <p className="text-xs text-blue-700 mt-1">
                        A second check at sign-in, so a stolen password alone can't get in.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Authenticator Setup Dialog */}
            <Dialog open={showAuthenticatorSetup} onOpenChange={setShowAuthenticatorSetup}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Setup Authenticator App</DialogTitle>
                  <DialogDescription>
                    Scan the QR code with your authenticator app
                  </DialogDescription>
                </DialogHeader>
                
                {authenticatorSetup && (
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      <img src={authenticatorSetup.qr_code} alt="QR Code" className="w-48 h-48" />
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground mb-2">Or enter this code manually:</p>
                      <code className="bg-muted px-3 py-2 rounded text-sm font-mono">{authenticatorSetup.manual_entry}</code>
                    </div>
                    <div className="space-y-2">
                      <Label>Enter the 6-digit code from your app</Label>
                      <Input 
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="000000"
                        maxLength={6}
                        className="text-center text-2xl tracking-widest"
                      />
                    </div>
                  </div>
                )}
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAuthenticatorSetup(false)}>Cancel</Button>
                  <Button 
                    onClick={() => verify2FA('authenticator', verificationCode)}
                    disabled={verificationCode.length !== 6}
                  >
                    Verify & Enable
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Email Verification Dialog */}
            <Dialog open={showEmailVerification} onOpenChange={setShowEmailVerification}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Verify Your Email</DialogTitle>
                  <DialogDescription>
                    We've sent a 6-digit code to your email
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Enter verification code</Label>
                    <Input 
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="000000"
                      maxLength={6}
                      className="text-center text-2xl tracking-widest"
                    />
                  </div>
                  <Button variant="link" className="text-sm" onClick={() => resend2FACode('email')}>
                    Didn't receive it? Resend code
                  </Button>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowEmailVerification(false)}>Cancel</Button>
                  <Button 
                    onClick={() => verify2FA('email', verificationCode)}
                    disabled={verificationCode.length !== 6}
                  >
                    Verify & Enable
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* SMS Setup Dialog */}
            <Dialog open={showSmsSetup} onOpenChange={setShowSmsSetup}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Setup SMS Verification</DialogTitle>
                  <DialogDescription>
                    Enter your phone number to receive verification codes
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  {!smsCodeSent ? (
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <Input 
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="+234 800 000 0000"
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Enter verification code sent to {phoneNumber}</Label>
                      <Input 
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="000000"
                        maxLength={6}
                        className="text-center text-2xl tracking-widest"
                      />
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setShowSmsSetup(false); setSmsCodeSent(false); }}>Cancel</Button>
                  {!smsCodeSent ? (
                    <Button onClick={sendSmsCode} disabled={!phoneNumber}>Send Code</Button>
                  ) : (
                    <Button 
                      onClick={() => verify2FA('sms', verificationCode)}
                      disabled={verificationCode.length !== 6}
                    >
                      Verify & Enable
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Privacy & Compliance Section */}
            <div className="pt-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Privacy & Compliance</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <PrivacySettingsTab />
            </div>

            </TabErrorBoundary>
          </TabsContent>

          {/* Banking Tab (Open Banking + Bank Accounts) */}
          <TabsContent value="banking" className="space-y-6">
            <TabErrorBoundary tabName="banking">

            {/* Bank Feed Sync Progress Banner */}
            <BankFeedSyncBanner bankAccounts={bankAccounts} />

            {/* Section: Open Banking Connections */}
            <Card data-highlight-key="connect-bank">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-[#108981]" />
                  Connect Your Bank
                </CardTitle>
                <CardDescription>
                  Transactions sync, categorise and match to your invoices and bills automatically.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* iter 908 audit - Country-Specific Open Banking Provider.
                    NG → Mono (live). US → Plaid (live; deep-link to /banks
                    for the full link flow). GB → "coming soon". All others
                    → "not available in your country". */}
                {business?.country === 'NG' ? (
                  // Bank Integration for Nigeria via Mono
                  <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed rounded-lg bg-muted/30">
                    <div className="w-16 h-16 rounded-full bg-[#108981]/10 flex items-center justify-center mb-4">
                      <Building className="w-8 h-8 text-[#108981]" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">
                      Connect {getCountryDisplayName(business?.country)} Bank
                    </h3>
                    <BankIntegrationTooltip countryCode={business?.country} showIcon>
                      <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                        Pulls transactions from your start month. Read-only, encrypted, never stored.
                      </p>
                    </BankIntegrationTooltip>
                    <Button 
                      onClick={openMonoWidget}
                      className="bg-[#108981] hover:bg-[#108981]/90"
                      disabled={!monoConfig.enabled}
                      data-testid="connect-bank-btn"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Connect Bank Account
                    </Button>
                    {!monoConfig.enabled && (
                      <p className="text-xs text-amber-600 mt-2">
                        <AlertCircle className="w-3 h-3 inline mr-1" />
                        Bank connection is currently being set up. Please try again shortly.
                      </p>
                    )}
                  </div>
                ) : business?.country === 'US' ? (
                  // Bank Integration for US via Plaid (live in production).
                  // We deep-link to /banks where the full Plaid Link flow lives.
                  <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed rounded-lg bg-muted/30">
                    <div className="w-16 h-16 rounded-full bg-[#108981]/10 flex items-center justify-center mb-4">
                      <Building className="w-8 h-8 text-[#108981]" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">Connect United States Bank</h3>
                    <BankIntegrationTooltip countryCode="US" showIcon>
                      <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                        12,000+ US banks. Read-only, encrypted, never stored.
                      </p>
                    </BankIntegrationTooltip>
                    <Button
                      onClick={() => navigate('/banks')}
                      className="bg-[#108981] hover:bg-[#108981]/90"
                      data-testid="connect-bank-btn-us"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Connect US bank or card
                    </Button>
                  </div>
                ) : business?.country === 'GB' ? (
                  // UK - Plaid licence pending; show explicit "coming soon".
                  <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed rounded-lg bg-muted/30" data-testid="connections-bank-coming-soon">
                    <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                      <Building className="w-8 h-8 text-amber-600" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">Bank connection: coming soon</h3>
                    <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                      UK bank sync is on the way. Until then, upload statements in the Bank Transactions Center.
                    </p>
                    <Badge variant="outline" className="text-amber-600 border-amber-300">Coming Soon</Badge>
                    <div className="mt-3 bg-[#108981]/5 border border-[#108981]/15 rounded-lg p-3 max-w-md text-left">
                      <p className="text-xs font-medium text-[#108981]">Recommended: Upload bi-weekly</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Every 2 weeks keeps your books current.
                      </p>
                    </div>
                  </div>
                ) : (
                  // All other countries - explicit "not available", not just "coming soon".
                  <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed rounded-lg bg-muted/30" data-testid="connections-bank-unavailable">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <Building className="w-8 h-8 text-gray-500" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">Bank connection not available in your country</h3>
                    <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                      Not yet available for {getCountryDisplayName(business?.country) || 'your region'}. Upload statements in the Bank Transactions Center instead.
                    </p>
                    <Badge variant="outline" className="text-amber-600 border-amber-300">Unavailable</Badge>
                    <div className="mt-3 bg-[#108981]/5 border border-[#108981]/15 rounded-lg p-3 max-w-md text-left">
                      <p className="text-xs font-medium text-[#108981]">Recommended: Upload bi-weekly</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Every 2 weeks keeps your books current.
                      </p>
                    </div>
                  </div>
                )}

                {/* Connected Banks (Mono connections for Nigeria only) */}
                {business?.country === 'NG' && monoConnections.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Connected Accounts ({monoConnections.length})
                    </h4>
                    <div className="grid gap-4">
                      {(Array.isArray(monoConnections) ? monoConnections : []).map((conn) => (
                        <div 
                          key={conn.id}
                          className="flex items-center justify-between p-4 border rounded-lg bg-background"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-[#283F8B]/10 flex items-center justify-center">
                              <Building className="w-6 h-6 text-[#283F8B]" />
                            </div>
                            <div>
                              <p className="font-medium">{conn.institution_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {conn.account_name} • ****{conn.account_number?.slice(-4)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Last synced: {conn.last_sync ? new Date(conn.last_sync).toLocaleString() : 'Never'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={conn.status === 'connected' ? 'default' : 'secondary'}>
                              {conn.status}
                            </Badge>
                            <span className="font-semibold text-[#108981]">
                              {business?.country === 'NG' ? '₦' : business?.country === 'GH' ? '₵' : 'KSh'}{conn.balance?.toLocaleString() || '0'}
                            </span>
                            {/* iter1078 - Manual sync icon removed. The balance
                                is auto-refreshed from Mono on the scheduled
                                sync (Celery Beat) and now sources from
                                bank_accounts.mono_balance so it matches the
                                bank reconciliation "balance as per bank
                                statement" surface. */}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => promptDisconnectMono(conn)}
                              data-testid={`disconnect-bank-${conn.id}`}
                            >
                              <Unlink className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prerequisites - shown only for supported countries (NG via Mono, US via Plaid) */}
                {(business?.country === 'NG' || business?.country === 'US') && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="bank-connection-prerequisites">
                    <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Before you connect
                    </h4>
                    <ul className="text-sm text-amber-800 space-y-1.5">
                      <li>• <strong>Internet/Mobile banking</strong> must be active on your bank account</li>
                      <li>• Have your <strong>phone nearby</strong>: your bank will send an OTP to verify</li>
                      <li>• AkountFly gets <strong>read-only access</strong>: we can never initiate transactions</li>
                      <li>• Works with both <strong>personal and business</strong> bank accounts</li>
                    </ul>
                  </div>
                )}

                {/* Info Box */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">How it works</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• All transactions are automatically synced from your bank</li>
                    <li>• Incoming payments are matched to your invoices</li>
                    <li>• Outgoing payments are matched to your bills</li>
                    <li>• Unmatched transactions create new records for review</li>
                    <li>• Everything flows into your General Ledger automatically</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section Divider: Bank Accounts */}
            <div className="flex items-center gap-2 pt-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Bank Accounts</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building className="w-5 h-5 text-[#283F8B]" />
                      Bank Accounts
                    </CardTitle>
                    <CardDescription>
                      Add bank accounts to receive payments from your customers
                    </CardDescription>
                  </div>
                  <Button onClick={openAddBankModal} disabled={checkingBankLimit} data-testid="add-bank-btn">
                    {checkingBankLimit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add Bank Account
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingBanks ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : bankAccounts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Building className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="font-medium text-foreground mb-2">No bank accounts yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Add a bank account to start receiving payments
                    </p>
                    <Button onClick={openAddBankModal} disabled={checkingBankLimit}>
                      {checkingBankLimit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Add Bank Account
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(Array.isArray(bankAccounts) ? bankAccounts : []).map((bank) => (
                      <div 
                        key={bank.id} 
                        className="p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                        data-testid={`bank-${bank.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#283F8B] to-[#108981] flex items-center justify-center">
                              <Building className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-foreground">{bank.bank_name}</p>
                                {bank.is_default && (
                                  <Badge variant="secondary" className="text-xs">Default</Badge>
                                )}
                                {business?.country === 'NG' && !bank.open_banking_connected && (
                                  <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Pending Connection</Badge>
                                )}
                                {bank.open_banking_connected && (
                                  <Badge variant="outline" className="text-xs text-[#108981] border-[#108981]/30">Connected</Badge>
                                )}
                                {bank.removal_state === 'removing' && (
                                  <Badge variant="outline" className="text-xs text-red-600 border-red-300 gap-1" data-testid={`bank-removing-${bank.id}`}>
                                    <Loader2 className="w-3 h-3 animate-spin" /> Removing…
                                  </Badge>
                                )}
                                {bank.needs_reconnect && !bank.open_banking_connected && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs text-amber-700 border-amber-300 cursor-pointer hover:bg-amber-50"
                                    onClick={() => navigate('/banks')}
                                    title="This account was restored. Reconnect it from My Banks to fetch the transactions since removal."
                                    data-testid={`bank-needs-reconnect-${bank.id}`}
                                  >
                                    Restored · Reconnect bank
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{bank.account_name}</p>
                              <p className="text-xs text-muted-foreground">
                                ****{bank.account_number?.slice(-4)} • {bank.currency}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditBankModal(bank)}
                              data-testid={`edit-bank-${bank.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {/* iter1113 - Archive (amber). Non-destructive,
                                keeps history, disconnects the provider,
                                hides from every future-use picker across
                                the platform. Reactivate from the
                                "Archived Bank Accounts" section below. */}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openArchiveDialog(bank)}
                              disabled={archivingBank === bank.id}
                              title="Archive - hide from future postings, keep history"
                              className="text-amber-600 hover:text-amber-700"
                              data-testid={`archive-bank-${bank.id}`}
                            >
                              {archivingBank === bank.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Archive className="w-4 h-4" />
                              )}
                            </Button>
                            {canRemoveBank && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => setRemoveTarget(bank)}
                                disabled={deletingBank === bank.id || bank.removal_state === 'removing'}
                                title="Remove - takes this account and everything posted through it out of your books (restorable)"
                                data-testid={`delete-bank-${bank.id}`}
                              >
                                {deletingBank === bank.id || bank.removal_state === 'removing' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        {/* NG unconnected warning */}
                        {business?.country === 'NG' && !bank.open_banking_connected && (
                          <div className="mt-2 ml-16 bg-amber-50 border border-amber-200 rounded-md p-2">
                            <p className="text-xs text-amber-700">
                              Connect to sync transactions automatically.
                            </p>
                          </div>
                        )}
                        {/* iter1091 - Reconciliation status + CTA. Was
                            previously a single green "Reconcile" button
                            with a check-mark icon that misled users
                            into believing the account was already
                            reconciled. Now surfaces the actual status
                            returned by /api/bank-accounts and offers a
                            provenance-aware CTA. */}
                        {bank.open_banking_connected && (() => {
                          const rs = bank.reconciliation_status || 'not_reconciled';
                          const commonBtn = 'text-xs h-7';
                          if (rs === 'reconciled') {
                            return (
                              <div className="mt-2 ml-16 flex items-center gap-2 text-xs" data-testid={`reconcile-status-${bank.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                <span className="text-emerald-700 font-medium">
                                  Reconciled
                                  {bank.last_reconciled_at ? (
                                    <span className="text-muted-foreground font-normal"> · {String(bank.last_reconciled_at).slice(0, 10)}</span>
                                  ) : null}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`${commonBtn} text-slate-600 hover:text-slate-800`}
                                  onClick={() => navigate(`/reconciliation?bank_id=${bank.id}`)}
                                  data-testid={`reconcile-${bank.id}`}
                                >
                                  <ArrowRight className="w-3.5 h-3.5 mr-1" />
                                  Open reconciliation
                                </Button>
                              </div>
                            );
                          }
                          const isVariance = rs === 'difference_exists';
                          const isItems = rs === 'items_to_reconcile';
                          const tone = isVariance
                            ? 'text-red-600 hover:text-red-700'
                            : isItems
                              ? 'text-amber-600 hover:text-amber-700'
                              : 'text-slate-600 hover:text-slate-700';
                          const Icon = isVariance || isItems ? AlertCircle : Scale;
                          const label = isVariance
                            ? (bank.last_variance != null
                                ? `Variance ${formatCurrency(bank.last_variance, bank.last_variance_currency || bank.currency)} · Reconcile now`
                                : 'Reconcile now')
                            : isItems
                              ? 'Items to reconcile'
                              : 'Start reconciliation';
                          return (
                            <div className="mt-2 ml-16 flex items-center gap-2" data-testid={`reconcile-status-${bank.id}`}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={`${commonBtn} ${tone}`}
                                onClick={async () => {
                                  // iter1234-hotfix - Phantom variance
                                  // self-heal (same behavior as My Banks
                                  // page). If GL is empty but provider
                                  // snapshot lingers from a pre-cascade-
                                  // fix statement delete, clear the
                                  // orphan snapshots first.
                                  if (isVariance) {
                                    const nb = bank.native_balance;
                                    const fb = bank.functional_balance;
                                    const glZero = Math.abs(nb || 0) < 0.01
                                                   && Math.abs(fb || 0) < 0.01;
                                    if (glZero) {
                                      try {
                                        const res = await axios.post(
                                          `${API}/bank-feeds/bank-accounts/${bank.id}/clear-phantom-snapshots`
                                        );
                                        if (res.data?.orphans_removed > 0) {
                                          toast.success(
                                            `Cleared ${res.data.orphans_removed} phantom balance snapshot(s). The variance should disappear on refresh.`
                                          );
                                          fetchBankAccounts?.();
                                          return;
                                        }
                                      } catch (_e) {
                                        // fall through to reconciliation page
                                      }
                                    }
                                  }
                                  navigate(`/reconciliation?bank_id=${bank.id}`);
                                }}
                                data-testid={`reconcile-${bank.id}`}
                              >
                                <Icon className="w-3.5 h-3.5 mr-1" />
                                {label} →
                              </Button>
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* iter1113 Phase 2 - Archived accounts with Reactivate + Permanently delete */}
            <ArchivedBankAccountsSection onChange={fetchBankAccounts} />

            {/* Remove & Restore - reversible quarantine of a bank account and everything posted through it */}
            <RemovedBankAccountsSection
              onChange={fetchBankAccounts}
              canManage={canRemoveBank}
              refreshKey={removedRefreshKey}
            />
            <RemoveBankAccountDialog
              bank={removeTarget}
              open={!!removeTarget}
              onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
              onStarted={() => { fetchBankAccounts(); setRemovedRefreshKey((k) => k + 1); }}
            />

            {/* iter1113 UI polish - Archive confirmation dialog. Sensitive
                action restricted to Settings > Banking only. NO typed-name
                confirmation - the amber icon + dialog copy are enough for
                a reversible action. */}
            <ConfirmDestructive
              name={pendingArchiveBank?.name || ''}
              impact={
                pendingArchiveBank?.isConnected
                  ? 'This will disconnect the bank connection and archive the account. It will no longer appear in any picker for new transactions, invoices, bills, transfers or statement uploads. Historical reports keep it. You can reactivate it later from Archived Bank Accounts below.'
                  : 'This will archive the account. It will no longer appear in any picker for new transactions, invoices, bills, transfers or statement uploads. Historical reports keep it. You can reactivate it later from Archived Bank Accounts below.'
              }
              destructiveLabel="Archive bank account"
              confirmingLabel="Archiving…"
              open={!!pendingArchiveBank}
              onOpenChange={(o) => { if (!o) setPendingArchiveBank(null); }}
              onConfirm={handleArchiveBank}
              testIdPrefix="confirm-archive-bank"
            />
            
            {/* Statement Vault - Auto-downloaded bank statements */}
            {(business?.country === 'NG' || business?.country === 'GH' || business?.country === 'KE') && (
              <StatementVault monoConnections={monoConnections} />
            )}

            </TabErrorBoundary>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-6">
            <TabErrorBoundary tabName="payments">
            {/* Header Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#283F8B]" />
                  Payment Integrations
                </CardTitle>
                <CardDescription>
                  Connect payment providers to accept online payments on your invoices. 
                  When you create an invoice, you can select which provider to use for the payment link.
                </CardDescription>
              </CardHeader>
            </Card>

            <StripeConnectSettings />

            {/* Info about payment provider selection */}
            <Card className="bg-gradient-to-r from-[#283F8B]/5 to-[#108981]/5 border-[#283F8B]/20">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#283F8B]/10 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-[#283F8B]" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">How Payment Links Work</h4>
                    <p className="text-sm text-muted-foreground">
                      Once you've connected a payment provider above, you can select it when creating an invoice. 
                      Your customer will receive a secure payment link in their invoice email. When they pay, 
                      the invoice is automatically marked as paid and funds are deposited to your connected bank account.
                    </p>
                    <Button 
                      variant="link" 
                      className="px-0 mt-2 text-[#108981]"
                      onClick={() => navigate('/invoices')}
                    >
                      Create an Invoice with Payment Link →
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bank Transfer Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#5FC6C5]" />
                  Bank Transfer
                </CardTitle>
                <CardDescription>
                  Always available - customers can pay via bank transfer
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-[#108981]" />
                  Your bank details will be shown on invoices automatically
                </div>
              </CardContent>
            </Card>

            {/* Section Divider: Third-Party Integrations */}
            <div className="flex items-center gap-2 pt-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Third-Party Integrations</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-[#108981]" />
                  Third-Party Integrations
                </CardTitle>
                <CardDescription>
                  Connect AkountFly with your favorite business tools for seamless data sync.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* CRM Integrations
                    iter1079 - Salesforce removed (paid subscription
                    starting $25/user/mo required for API access; free
                    Trailhead orgs cannot connect real business data). */}
                <div>
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    CRM & Sales
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                          <span className="font-bold text-orange-600">HS</span>
                        </div>
                        <div>
                          <p className="font-medium">HubSpot</p>
                          <p className="text-xs text-muted-foreground">Marketing and sales automation</p>
                        </div>
                      </div>
                      <Badge variant="secondary">Coming Soon</Badge>
                    </div>
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <span className="font-bold text-red-600">ZO</span>
                        </div>
                        <div>
                          <p className="font-medium">Zoho CRM</p>
                          <p className="text-xs text-muted-foreground">Customer relationship management</p>
                        </div>
                      </div>
                      <Badge variant="secondary">Coming Soon</Badge>
                    </div>
                  </div>
                </div>

                {/* E-commerce Integrations
                    iter1079 - Shopify removed (requires paid $29/mo store).
                    WooCommerce kept - free open-source plugin with a free REST API. */}
                <div>
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    E-commerce & Inventory
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <span className="font-bold text-purple-600">WC</span>
                        </div>
                        <div>
                          <p className="font-medium">WooCommerce</p>
                          <p className="text-xs text-muted-foreground">WordPress store integration</p>
                        </div>
                      </div>
                      <Badge variant="secondary">Coming Soon</Badge>
                    </div>
                  </div>
                </div>

                {/* POS Integrations
                    iter1079 - Clover removed (requires paid merchant account
                    and partnership approval). Square kept - free developer
                    sandbox + free merchant signup. */}
                <div>
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-muted-foreground" />
                    Point of Sale
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <span className="font-bold text-red-600">SQ</span>
                        </div>
                        <div>
                          <p className="font-medium">Square</p>
                          <p className="text-xs text-muted-foreground">POS and payment processing</p>
                        </div>
                      </div>
                      <Badge variant="secondary">Coming Soon</Badge>
                    </div>
                  </div>
                </div>

                {/* Productivity Integrations */}
                <div>
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    Productivity
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                          <span className="font-bold text-yellow-600">GS</span>
                        </div>
                        <div>
                          <p className="font-medium">Google Sheets</p>
                          <p className="text-xs text-muted-foreground">Export data to spreadsheets</p>
                        </div>
                      </div>
                      <Badge variant="secondary">Coming Soon</Badge>
                    </div>
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <span className="font-bold text-indigo-600">SL</span>
                        </div>
                        <div>
                          <p className="font-medium">Slack</p>
                          <p className="text-xs text-muted-foreground">Get notifications in Slack</p>
                        </div>
                      </div>
                      <Badge variant="secondary">Coming Soon</Badge>
                    </div>
                  </div>
                </div>

                {/* Request Integration */}
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    Don't see the integration you need?
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => toast.info('Integration request feature coming soon!')}
                  >
                    Request an Integration
                  </Button>
                </div>
              </CardContent>
            </Card>

            </TabErrorBoundary>
          </TabsContent>

          {/* Scheduled Reports & Tips Tab */}
          <TabsContent value="schedules" className="space-y-6">
            <TabErrorBoundary tabName="schedules">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-[#283F8B]" />
                      Scheduled Reports
                    </CardTitle>
                    <CardDescription>
                      Automatically receive financial reports via email
                    </CardDescription>
                  </div>
                  <Button onClick={openAddScheduleModal} data-testid="add-schedule-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Schedule
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingSchedules ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : scheduledReports.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                      <Calendar className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="font-medium text-foreground mb-2">No scheduled reports</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Set up automatic report delivery to your inbox
                    </p>
                    <Button onClick={openAddScheduleModal}>
                      <Plus className="w-4 h-4 mr-2" />
                      Schedule Your First Report
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(Array.isArray(scheduledReports) ? scheduledReports : []).map((schedule) => (
                      <div 
                        key={schedule.id}
                        className={`flex items-center justify-between p-4 rounded-lg border ${schedule.is_active ? 'border-border' : 'border-dashed border-muted-foreground/30 bg-muted/30'}`}
                        data-testid={`schedule-${schedule.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${schedule.is_active ? 'bg-[#283F8B]/10' : 'bg-muted'}`}>
                            <FileText className={`w-6 h-6 ${schedule.is_active ? 'text-[#283F8B]' : 'text-muted-foreground'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${schedule.is_active ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {schedule.report_name}
                              </p>
                              <Badge variant={schedule.is_active ? 'default' : 'secondary'} className="text-xs">
                                {schedule.frequency}
                              </Badge>
                              {!schedule.is_active && <Badge variant="outline" className="text-xs">Paused</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              To: {schedule.recipients?.join(', ')}
                            </p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <Clock className="w-3 h-3" />
                              Next: {formatNextRun(schedule.next_run)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleSendNow(schedule.id)}
                            disabled={sendingReport === schedule.id}
                            title="Send now"
                          >
                            {sendingReport === schedule.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleToggleSchedule(schedule.id)}
                            title={schedule.is_active ? 'Pause' : 'Activate'}
                          >
                            {schedule.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditScheduleModal(schedule)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteSchedule(schedule.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Section Divider: Tips & Automation */}
            <div className="flex items-center gap-2 pt-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tips & Automation</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <AutomationTipsSection />

            </TabErrorBoundary>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <TabErrorBoundary tabName="notifications">
              <NotificationPreferencesTab />
            </TabErrorBoundary>
          </TabsContent>

          <TabsContent value="activity-log" className="space-y-6">
            <SettingsErrorBoundary tabName="activity-log">
              <ActivityLogTab />
            </SettingsErrorBoundary>
          </TabsContent>

          {/* Projects Tab (iter 1003) */}
          <TabsContent value="projects" className="space-y-6">
            <SettingsErrorBoundary tabName="projects">
              <ProjectsSettingsTab />
            </SettingsErrorBoundary>
          </TabsContent>
        </Tabs>

        {/* Scheduled Report Modal */}
        <Dialog open={scheduleModalOpen} onOpenChange={setScheduleModalOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="schedule-modal">
            <DialogHeader>
              <DialogTitle>
                {editingSchedule ? 'Edit Scheduled Report' : 'Schedule a Report'}
              </DialogTitle>
              <DialogDescription>
                Set up automatic report delivery to your inbox
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Report Type</Label>
                <Select 
                  value={scheduleForm.report_type} 
                  onValueChange={(value) => setScheduleForm(prev => ({ ...prev, report_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reportTypes.map(rt => (
                      <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select 
                  value={scheduleForm.frequency} 
                  onValueChange={(value) => setScheduleForm(prev => ({ ...prev, frequency: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {scheduleForm.frequency === 'weekly' && (
                <div className="space-y-2">
                  <Label>Day of Week</Label>
                  <Select 
                    value={String(scheduleForm.day_of_week)} 
                    onValueChange={(value) => setScheduleForm(prev => ({ ...prev, day_of_week: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {daysOfWeek.map((day, idx) => (
                        <SelectItem key={idx} value={String(idx)}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {scheduleForm.frequency === 'monthly' && (
                <div className="space-y-2">
                  <Label>Day of Month</Label>
                  <Select 
                    value={String(scheduleForm.day_of_month)} 
                    onValueChange={(value) => setScheduleForm(prev => ({ ...prev, day_of_month: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(28)].map((_, idx) => (
                        <SelectItem key={idx + 1} value={String(idx + 1)}>{idx + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Time</Label>
                <Input 
                  type="time"
                  value={scheduleForm.time_of_day}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, time_of_day: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Recipients (comma separated)</Label>
                <Input 
                  value={scheduleForm.recipients}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, recipients: e.target.value }))}
                  placeholder="email1@example.com, email2@example.com"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center space-x-2">
                  <Switch 
                    id="include_comparison"
                    checked={scheduleForm.include_comparison}
                    onCheckedChange={(checked) => setScheduleForm(prev => ({ ...prev, include_comparison: checked }))}
                  />
                  <Label htmlFor="include_comparison" className="text-sm">Include period comparison</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setScheduleModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveSchedule} 
                disabled={savingSchedule}
                className="bg-[#283F8B] hover:bg-[#283F8B]/90"
              >
                {savingSchedule ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingSchedule ? 'Update Schedule' : 'Create Schedule'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bank Account Modal - Using reusable component */}
        <AddBankAccountModal 
          open={bankModalOpen} 
          onOpenChange={setBankModalOpen}
          onSuccess={fetchBankAccounts}
          editingBank={editingBank}
        />

        {/* Bank Account Overage Warning */}
        <AlertDialog open={bankOverageWarning} onOpenChange={setBankOverageWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-500" />
                Extra Bank Account Charge
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    Your <strong>{bankOverageInfo?.plan_name}</strong> plan includes <strong>{bankOverageInfo?.limit}</strong> bank account{bankOverageInfo?.limit > 1 ? 's' : ''}.
                    You currently have <strong>{bankOverageInfo?.current}</strong>.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="font-medium text-amber-800 text-sm">
                      Adding another bank account will cost{' '}
                      <strong>{bankOverageInfo?.currency === 'NGN' ? '₦' : '$'}{bankOverageInfo?.overage_cost?.toLocaleString()}/month</strong>{' '}
                      as an overage charge.
                    </p>
                  </div>
                  {bankOverageInfo?.next_plan && (
                    <p className="text-xs text-muted-foreground">
                      Upgrade to <strong>{bankOverageInfo.next_plan.name}</strong> ({bankOverageInfo.next_plan.price}) to get {bankOverageInfo.next_plan.bank_accounts_limit} bank accounts included.
                    </p>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="overage-bank-cancel">Cancel</AlertDialogCancel>
              {bankOverageInfo?.next_plan && (
                <Button
                  variant="outline"
                  onClick={() => { setBankOverageWarning(false); navigate('/billing'); }}
                  className="gap-1.5"
                  data-testid="overage-bank-upgrade"
                >
                  <ArrowUpRight className="w-4 h-4" />
                  Upgrade Plan
                </Button>
              )}
              <AlertDialogAction
                onClick={() => { setBankOverageWarning(false); setBankModalOpen(true); }}
                className="bg-[#108981] hover:bg-[#108981]/90"
                data-testid="overage-bank-continue"
              >
                Continue & Add Bank
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Staged Mono Sync Progress Dialog */}
      <MonoSyncProgress
        open={!!monoSyncStatus}
        syncStatus={monoSyncStatus}
        syncMessage={monoSyncMessage}
        backlogInfo={monoBacklogInfo}
        confirmingBacklog={monoConfirmingBacklog}
        onConfirmBacklog={hookConfirmBacklog}
        onRollback={hookRollbackConnection}
        onDismiss={() => { hookDismissBacklog(); monoResetState(); }}
        preflightData={monoPreflightData}
        preflightLoading={monoPreflightLoading}
        onNavigateToBankFeed={() => navigate('/bank-feeds')}
      />

      {/* ── Disconnect Bank Warning Dialog ── */}
      <AlertDialog open={!!disconnectTarget} onOpenChange={(open) => !open && setDisconnectTarget(null)}>
        <AlertDialogContent className="sm:max-w-md" data-testid="settings-disconnect-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Disconnect {disconnectTarget?.bankName}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p className="font-medium text-gray-700">This action will:</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                <li>Stop live transaction sync from this bank</li>
                <li>Stop daily balance updates for reconciliation</li>
                <li>Disable automatic bank statement fetching</li>
              </ul>
              <p className="font-medium text-gray-700 pt-1">This will NOT delete:</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                <li>Existing transactions already synced</li>
                <li>Journal entries and GL postings</li>
                <li>The bank account record in your Chart of Accounts</li>
              </ul>
              <p className="text-xs text-gray-500 pt-2 italic">
                You can reconnect later, but you may be charged a backlog fee for the gap period (bank charges excluded).
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-disconnect-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisconnectMono}
              disabled={disconnecting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="settings-disconnect-confirm"
            >
              {disconnecting ? 'Disconnecting...' : 'Yes, Disconnect'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

const SettingsPageWithBoundary = () => (
  <SettingsErrorBoundary>
    <SettingsPage />
  </SettingsErrorBoundary>
);

export default SettingsPageWithBoundary;
